import type {
  CellClassParams,
  ColDef,
  EditableCallbackParams,
  ICellRendererParams,
} from 'ag-grid-community';
import { startCase } from 'lodash';
import type { FieldValueType } from 'api/tasks';
import { getCellEditorPopupPosition } from 'components/atoms/grid/utils';
import { dueDateRenderer } from 'components/organisms/task-grid/plugins/with-due-date-column/due-date-renderer';
import { dueDateValueGetter } from 'components/organisms/task-grid/plugins/with-due-date-column/due-date-value-getter';
import type { Translate } from 'hooks/use-translation';
import type { Field, TaskField } from 'stores/fields';
import { hasWriteAccessForField } from 'utils/field-access';
import { queueMacrotask } from 'utils/macrotask';
import { getComparator as getBaseComparator } from '../comparators';
import { getEditor } from '../editors';
import { FieldType, FilterableField } from '../fields';
import {
  getRenderer as getBaseRenderer,
  getHeaderTemplate,
  getValueFormatter,
} from '../renderers';
import type { TaskUpdateRequest } from '../use-update-tasks';
import { suppressEnterAndTabKey, suppressEnterKey } from '../utils';
import { getValueGetter as getBaseValueGetter } from '../value-getter';
import { checkboxColumn } from './checkbox-column';
import { taskColumn } from './task-column';

export const getRenderer = (
  field: FilterableField,
  projectBillabilityChecker: (projectId?: number) => boolean = () => false,
) => {
  switch (field.fieldName) {
    case 'dueDate':
      return ({ value }) => dueDateRenderer(value);
    default:
      return getBaseRenderer(field, projectBillabilityChecker);
  }
};

const getComparator = (field: FilterableField) => {
  switch (field.fieldName) {
    case 'dueDate':
      return (a, b) => {
        const a_due_date = (a?.dueDate as string) ?? '';
        const b_due_date = (b?.dueDate as string) ?? -1;

        return a_due_date === b_due_date ? 0 : a_due_date < b_due_date ? -1 : 1;
      };
    default:
      return getBaseComparator(field);
  }
};

export const getValueGetter = (field: FilterableField) => {
  switch (field.fieldName) {
    case 'dueDate':
      return dueDateValueGetter;
    default:
      return getBaseValueGetter(field);
  }
};

export const excludedFields = ['status', 'taskName', 'overdue', 'type'];
export const timeTrackedFields = ['timeEntryMinutes'];
export const hideFromExternalUser = ['timeEntryMinutes'];
const nonEditableComputedFields = [
  'timeEntryMinutes',
  'atRisk',
  'createdAt',
  'createdBy',
  'responsible',
];

export const isCellEditable = (
  field: Field | FilterableField,
  data: Record<string, any>,
  projectBillabilityChecker: (projectId?: number) => boolean = () => false,
  checkIsProjectOnTaskEffortPlanning: (projectId?: number) => boolean = () =>
    true,
) => {
  const isRbacEditable =
    (field.fieldIndex != null
      ? hasWriteAccessForField(field, data?.fieldsAccessMetaInBase3)
      : true) && !nonEditableComputedFields.includes(field.fieldName);

  if (field.fieldName === 'completedAt') {
    return isRbacEditable && data?.completedAt != null;
  }
  if (field.fieldName === 'startDateActual') {
    return isRbacEditable && data?.startDateActual != null;
  }
  if (field.fieldName === 'sprint' || field.fieldName === 'associatedSprints') {
    return false;
  }

  if (field.fieldName === 'effort') {
    return (
      checkIsProjectOnTaskEffortPlanning(data?.project as number) &&
      isRbacEditable
    );
  }

  if (field.fieldName !== 'defaultTimeEntryBillable') {
    return isRbacEditable;
  }

  const projectId = data?.project;

  const isProjectNonBillable =
    projectId == null ? false : projectBillabilityChecker(projectId as number);

  return isProjectNonBillable ? false : isRbacEditable;
};

export const getIsEditable =
  (
    field: Field | FilterableField,
    value: boolean,
    projectBillabilityChecker: (projectId?: number) => boolean = () => false,
    checkIsProjectOnTaskEffortPlanning: (projectId?: number) => boolean = () =>
      true,
  ) =>
  (params: EditableCallbackParams) => {
    if (params.node.id === 'provisional') {
      return false;
    }

    return (
      value &&
      isCellEditable(
        field,
        params.data,
        projectBillabilityChecker,
        checkIsProjectOnTaskEffortPlanning,
      )
    );
  };

const getHeaderName = (field: FilterableField) => {
  if (field.fieldLabel?.includes('Points')) {
    return 'Points';
  }

  return field.fieldLabel ?? startCase(field.fieldName);
};

export const getColumnDef =
  (
    updatedReadonlyFields: string[],
    translate: Translate,
    checkIsProjectNonBillable: (projectId?: number) => boolean,
    checkIsProjectOnTaskEffortPlanning: (projectId?: number) => boolean,
  ) =>
  (field: FilterableField): ColDef => {
    const editable = !updatedReadonlyFields.includes(field.fieldName);
    const isEditableFn = getIsEditable(
      field,
      editable,
      checkIsProjectNonBillable,
      checkIsProjectOnTaskEffortPlanning,
    );

    return {
      field: field.fieldName,
      headerName: getHeaderName(field),
      cellRenderer: getRenderer(field, checkIsProjectNonBillable),
      cellRendererParams: { translate },
      cellRendererSelector: ({ node }: ICellRendererParams) =>
        node.id === 'provisional'
          ? {
              component: () => '',
            }
          : undefined,
      valueGetter: getValueGetter(field),
      valueFormatter: getValueFormatter(field),
      comparator: getComparator(field),
      initialWidth: field.type === FieldType.MultiLineText ? 300 : 140,
      headerTooltip: field.fieldLabel ?? startCase(field.fieldName),
      cellClass: (params: CellClassParams) =>
        isEditableFn(params) ? 'grid-cell-editable' : 'grid-cell-not-editable',
      editable: isEditableFn,
      sortable: field.fieldName !== 'startDateActual',
      cellEditor: editable ? getEditor(field as TaskField) : null,
      cellEditorPopupPosition: getCellEditorPopupPosition(field),
      suppressKeyboardEvent:
        field.type === FieldType.MultiLineText
          ? suppressEnterAndTabKey
          : suppressEnterKey,
    } as ColDef;
  };

interface ToColumnDef {
  translate: Translate;
  isTimesheetFeatureEnabled: boolean;
  updatedReadonlyFields: string[];
  isNativeUser: boolean;
  checkIsProjectNonBillable: (projectId?: number) => boolean;
  checkIsProjectOnTaskEffortPlanning: (projectId?: number) => boolean;
  rowSelectIconColumn?: ColDef;
  hideRowSelectIcon?: boolean;
  initialWidth?: number;
}

export const toColumnDef =
  ({
    translate,
    isTimesheetFeatureEnabled,
    updatedReadonlyFields,
    isNativeUser,
    checkIsProjectNonBillable,
    rowSelectIconColumn,
    hideRowSelectIcon,
    initialWidth,
    checkIsProjectOnTaskEffortPlanning,
  }: ToColumnDef) =>
  (fields: FilterableField[]): ColDef[] => {
    if (fields.length === 0) {
      return [];
    }

    return [
      ...(hideRowSelectIcon ? [] : [rowSelectIconColumn || checkboxColumn]),
    ].concat([
      taskColumn(fields, true, initialWidth),
      ...fields.reduce((acc, field) => {
        if (
          !excludedFields.includes(field.fieldName) &&
          (isTimesheetFeatureEnabled ||
            !timeTrackedFields.includes(field.fieldName)) &&
          (isNativeUser || !hideFromExternalUser.includes(field.fieldName))
        ) {
          acc.push(
            getColumnDef(
              updatedReadonlyFields,
              translate,
              checkIsProjectNonBillable,
              checkIsProjectOnTaskEffortPlanning,
            )(field),
          );
        }

        return acc;
      }, [] as ColDef[]),
    ]);
  };

export const withOnUpdate =
  (onUpdate: (request: TaskUpdateRequest[]) => void) =>
  (columns: ColDef[]): ColDef[] => {
    const onChange = (
      { filtered, task, checkbox, ...record }: Record<string, unknown>,
      field: TaskField,
      from: FieldValueType,
      to: FieldValueType,
      callback?: VoidFunction,
    ) =>
      queueMacrotask(() => {
        if (record.taskId !== 'provisional') {
          onUpdate([{ record, updates: [{ field, from, to }], callback }]);
        }
      });

    return columns.map((col) => ({
      ...col,
      cellEditorParams: col.editable
        ? { ...col.cellEditorParams, onUpdate: onChange }
        : {},
    }));
  };
const PROJECT_COLUMN_PREFIX = 'project-';

const getProjectColumnDef =
  (translate: Translate) =>
  (field: FilterableField): ColDef =>
    ({
      field: `${PROJECT_COLUMN_PREFIX}${field.fieldName}`,
      headerName: field.fieldLabel ?? startCase(field.fieldName),
      cellRenderer: getRenderer(field),
      valueGetter: getValueGetter(field),
      valueFormatter: getValueFormatter(field),
      comparator: getComparator(field),
      width: field.type === FieldType.MultiLineText ? 300 : 140,
      headerComponentParams: {
        template: getHeaderTemplate({
          field,
          editable: false,
          translate,
          isProjectHeader: true,
        }),
      },
      editable: false,
      initialHide: false,
      cellClass: ['grid-cell-not-editable'],
      suppressKeyboardEvent: () => true,
    } as ColDef);

export const toProjectColumns =
  (
    projectFields: FilterableField[],
    translate: Translate,
    includedProjectFields: string[],
  ) =>
  (columns: ColDef[]): ColDef[] => {
    return [
      ...columns,
      ...(includedProjectFields.length > 0
        ? projectFields
            .filter((field) => includedProjectFields.includes(field.fieldName))
            .map(getProjectColumnDef(translate))
        : []),
    ];
  };
